import { useState } from "react";
import { observer } from "mobx-react-lite";
import { LearningStore } from "./learning_store";
import { PageHeading } from "./learning_lobby";

export const BuilderScreen = observer(
  ({ store, go }: { store: LearningStore; go: (id?: string) => void }) => {
    const [counts, setCounts] = useState<Record<string, number>>({});
    const [title, setTitle] = useState("Мой вариант");
    const [score, setScore] = useState("100");
    const [error, setError] = useState("");
    const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
    function setCount(tag: string, count: number, available: number) {
      setCounts((previous) => ({
        ...previous,
        [tag]: Math.max(0, Math.min(available, 100, count)),
      }));
    }
    async function submit(event: React.FormEvent) {
      event.preventDefault();
      if (
        !title.trim() ||
        total < 1 ||
        total > 100 ||
        !Number.isFinite(Number(score)) ||
        Number(score) < 0.01 ||
        Number(score) > 1000000 ||
        Math.abs(Number(score) * 100 - Math.round(Number(score) * 100)) >
          0.000001
      ) {
        setError(
          "Введите название, выберите от 1 до 100 заданий и укажите общий балл от 0,01 до 1 000 000 (до двух знаков после запятой).",
        );
        return;
      }
      setError("");
      const id = await store.construct(
        title,
        Object.entries(counts)
          .filter(([, count]) => count > 0)
          .map(([tag, count]) => ({ tag, count })),
        Number(score),
      );
      if (id) go(id);
    }
    return (
      <>
        <PageHeading
          title="Создай свой вариант"
          description="Выбери темы и количество заданий."
        />
        <form className="builder-layout" onSubmit={submit}>
          <section
            className="builder-topics"
            aria-label="Темы для конструктора"
          >
            {store.randomTagsError ? (
              <div role="alert">
                <p>{store.randomTagsError}</p>
                <button
                  type="button"
                  disabled={store.busy}
                  onClick={() => store.refreshRandomTags()}
                >
                  Загрузить теги
                </button>
              </div>
            ) : (
              !store.randomTags.length && (
                <p>
                  {store.busy
                    ? "Загрузка тем…"
                    : "Нет заданий для конструктора."}
                </p>
              )
            )}
            {store.randomTags.map((topic, index) => (
              <article className="builder-topic" key={topic.tag}>
                <span className="topic-number" aria-hidden="true">
                  {index + 1}
                </span>
                <h2>{topic.tag}</h2>
                <div className="quantity-control">
                  <button
                    type="button"
                    aria-label={`Уменьшить: ${topic.tag}`}
                    disabled={store.busy || !counts[topic.tag]}
                    onClick={() =>
                      setCount(
                        topic.tag,
                        (counts[topic.tag] || 0) - 1,
                        topic.count,
                      )
                    }
                  >
                    −
                  </button>
                  <output aria-label={`Выбрано: ${topic.tag}`}>
                    {counts[topic.tag] || 0} / {topic.count}
                  </output>
                  <button
                    type="button"
                    aria-label={`Добавить: ${topic.tag}`}
                    disabled={
                      store.busy ||
                      total >= 100 ||
                      (counts[topic.tag] || 0) >= topic.count
                    }
                    onClick={() =>
                      setCount(
                        topic.tag,
                        (counts[topic.tag] || 0) + 1,
                        topic.count,
                      )
                    }
                  >
                    +
                  </button>
                </div>
              </article>
            ))}
          </section>
          <aside className="portal-card builder-summary">
            <label htmlFor="variant-title">Название</label>
            <input
              id="variant-title"
              value={title}
              maxLength={300}
              onChange={(event) => setTitle(event.target.value)}
              disabled={store.busy}
            />
            <div className="builder-total">
              <p>Заданий</p>
              <strong>{total}</strong>
            </div>
            <label htmlFor="variant-score">Общий балл</label>
            <input
              id="variant-score"
              value={score}
              inputMode="decimal"
              onChange={(event) => setScore(event.target.value)}
              disabled={store.busy}
            />
            {error && (
              <p role="alert" className="portal-form-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              className="portal-primary"
              disabled={store.busy || !total}
            >
              Создать вариант
            </button>
            {total > 0 && (
              <button
                type="button"
                disabled={store.busy}
                onClick={() => setCounts({})}
              >
                Сбросить выбор
              </button>
            )}
          </aside>
        </form>
      </>
    );
  },
);
